import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log, error as logError } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import '../contracts/ComboPrices_SalvarInput';
import '../contracts/ComboPrices_RemoverInput';
import '../contracts/ComboPrices_ListaPorFranquiaResponse';
import '../contracts/ComboPrices_ListaVendaveisResponse';
import '../contracts/ComboPrices_SalvarResponse';
import '../contracts/ComboPrices_RemoverResponse';
import type { ComboPrices_PriceRow } from '../contracts/ComboPrices_PriceRow';
import type { ComboPrices_SalvarInput } from '../contracts/ComboPrices_SalvarInput';
import type { ComboPrices_RemoverInput } from '../contracts/ComboPrices_RemoverInput';

/**
 * Preço de combo POR UNIDADE (NS 2026-0090), a irmã de
 * useSOAdditionalServicePrices com uma diferença: sem linha ativa a unidade NÃO
 * vende o combo — não há fallback global. Remover é `active = 0`.
 *
 * As listagens falam UNIDADES da moeda local; `salvar` fala CENTAVOS (como a
 * matriz). A conversão é de quem monta o payload, o form.
 */
export function useSOComboPrices() {
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const basePath = '/index.php/so/combo_prices';

  const fetchByFranchise = async (franchiseId: number): Promise<ComboPrices_PriceRow[]> => {
    log('useSOComboPrices', 'fetchByFranchise', 'Fetching prices', { franchiseId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/lista_por_franquia/${franchiseId}`, { responseContract: 'ComboPrices_ListaPorFranquiaResponse' });
      if (response.success) {
        return (response.dados || []) as ComboPrices_PriceRow[];
      }
      throw new Error(response.error || 'Erro ao buscar os preços dos combos');
    } catch (err) {
      logError('useSOComboPrices', 'fetchByFranchise', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /** O que a unidade VENDE: combo ativo na rede com preço ativo nela. */
  const fetchSellable = async (franchiseId: number): Promise<ComboPrices_PriceRow[]> => {
    log('useSOComboPrices', 'fetchSellable', 'Fetching sellable combos', { franchiseId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/lista_vendaveis/${franchiseId}`, { responseContract: 'ComboPrices_ListaVendaveisResponse' });
      if (response.success) {
        return (response.dados || []) as ComboPrices_PriceRow[];
      }
      throw new Error(response.error || 'Erro ao buscar os combos da unidade');
    } catch (err) {
      logError('useSOComboPrices', 'fetchSellable', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const save = async (data: ComboPrices_SalvarInput) => {
    log('useSOComboPrices', 'save', 'Saving price', data);
    isLoading.value = true;
    error.value = null;
    try {
      validate('ComboPrices_SalvarInput', data);
      const response = await httpService.post(`${basePath}/salvar`, data, { responseContract: 'ComboPrices_SalvarResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao salvar o preço do combo');
    } catch (err) {
      logError('useSOComboPrices', 'save', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const remove = async (data: ComboPrices_RemoverInput) => {
    log('useSOComboPrices', 'remove', 'Disabling price', data);
    isLoading.value = true;
    error.value = null;
    try {
      validate('ComboPrices_RemoverInput', data);
      const response = await httpService.post(`${basePath}/remover`, data, { responseContract: 'ComboPrices_RemoverResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao desligar o combo na unidade');
    } catch (err) {
      logError('useSOComboPrices', 'remove', 'Error', err);
      error.value = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    error,
    fetchByFranchise,
    fetchSellable,
    save,
    remove,
  };
}
